import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import Image from '@atlaskit/image';
import Portal from '@atlaskit/portal';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import {
	type DismissEvent,
	PopoverContent,
	PopoverProvider,
	PopoverTarget,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightMedia,
	SpotlightPrimaryAction,
	UNSAFE_UpdateOnChange as SpotlightUpdateOnChange,
} from '@atlaskit/spotlight';
import { token } from '@atlaskit/tokens';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { UNIVERSAL_CREATE_CWR_EXPERIMENT_SPOTLIGHT_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';

import SplitButtonGif from '../assets/SplitButton.gif';

import { useCwRSpotlightSeenStorage } from './useCwRSpotlightSeenStorage.tsx';
import { useElementBySelector } from './useElementBySelector.ts';

/**
 * Hidden inner anchor rendered as the first child of the universal-create
 * menu by `MenuContent` (in `platform/.../universal-create`). The platform
 * provides this element specifically so consumers can `createPortal` a
 * popover target into it without disturbing the menu's layout.
 */
const MENU_SELECTOR = '[data-testid="global-create-content-popover-menu--hidden"]';

type UniversalCreateCwRSpotlightOnboardingKey =
	(typeof UNIVERSAL_CREATE_CWR_EXPERIMENT_SPOTLIGHT_ONBOARDING_STATE_KEYS)[keyof typeof UNIVERSAL_CREATE_CWR_EXPERIMENT_SPOTLIGHT_ONBOARDING_STATE_KEYS];
const ONBOARDING_STATE_KEYS_TO_FETCH = Object.values(
	UNIVERSAL_CREATE_CWR_EXPERIMENT_SPOTLIGHT_ONBOARDING_STATE_KEYS,
) as UniversalCreateCwRSpotlightOnboardingKey[];

const CHOREOGRAPHER_MESSAGE_ID = 'UniversalCreateCwRExperimentSpotlight';
const ANALYTICS_SOURCE = 'universalCreateCwRExperimentSpotlight';

const i18n = defineMessages({
	title: {
		id: 'create-blank-fabric-page.universal-create.menu-spotlight.title',
		defaultMessage: 'We’re evolving how work starts',
		description: 'Title for the universal create menu spotlight',
	},
	bodyLine1: {
		id: 'create-blank-fabric-page.universal-create.menu-spotlight.body-line1',
		defaultMessage:
			'Looking to Create with Rovo? Choose <bold>Create</bold> to get started instantly with Rovo’s help.',
		description:
			'First paragraph of the universal create menu spotlight body. The <bold></bold> tag is rendered as bold text.',
	},
	bodyLine2: {
		id: 'create-blank-fabric-page.universal-create.menu-spotlight.body-line2',
		defaultMessage: 'Or select the dropdown to jump into a doc, whiteboard, slides, or database.',
		description: 'Second paragraph of the universal create menu spotlight body.',
	},
	cta: {
		id: 'create-blank-fabric-page.universal-create.menu-spotlight.cta',
		defaultMessage: 'Show me',
		description: 'CTA button text for the universal create menu spotlight',
	},
});

const spotlightAnchorStyles = cssMap({
	anchorWrapper: {
		display: 'block',
		position: 'absolute',
		top: token('space.400'),
		left: token('space.negative.100'),
		width: '1px',
		height: '1px',
	},
});

const BODY_LINE1_VALUES = {
	bold: (chunks: React.ReactNode) => <Text weight="bold">{chunks}</Text>,
};

export type UseUniversalCreateMenuSpotlightArgs = {
	isMenuOpen: boolean;
	onCtaDismissMenu?: () => void;
};

export type UseUniversalCreateMenuSpotlightResult = {
	spotlight: React.ReactNode;
	startSpotlight: () => void;
	stopSpotlight: () => void;
};

/**
 * Spotlight anchored against the universal-create menu dropdown via the
 * public `data-testid="global-create-content-popover-menu"` attribute on the
 * menu list. Uses the Confluence Choreographer to arbitrate timing and the
 * onboarding-state service to persist a "shown once" flag.
 *
 * The hook returns both the spotlight `ReactNode` and an imperative
 * `stopSpotlight` callback. Consumers wire `stopSpotlight` into their menu
 * `onClose` event handler (or any other event handler that should release
 * the spotlight); the hook itself never calls `stopMessage` from a
 * `useEffect`.
 *
 * SSR / hydration safety:
 *   - All DOM access (selector lookup, `createPortal`, `document.body`) lives
 *     behind `useElementBySelector` (effect-only, guarded by `isClientSide()`
 *     from `@confluence/meta`), so nothing renders on the server.
 *   - `spotlight` is `null` on the server and on the very first client
 *     render — portals are only mounted after `useEffect` runs and resolves
 *     the anchor element. This guarantees hydration is unaffected.
 */
export const useUniversalCreateMenuSpotlight = ({
	isMenuOpen,
	onCtaDismissMenu,
}: UseUniversalCreateMenuSpotlightArgs): UseUniversalCreateMenuSpotlightResult => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setOnboardingState } = useSetOnboardingState();
	const { useChoreographer } = useChoreographerAssets();
	const { markSpotlightSeen: markSpotlightSeenInStorage } = useCwRSpotlightSeenStorage();

	const [isSpotlightActive, setIsSpotlightActive] = useState(false);

	const {
		data: onboardingStateData,
		loading: isOnboardingStateLoading,
		error: isOnboardingStateError,
	} = useGetOnboardingState(ONBOARDING_STATE_KEYS_TO_FETCH);

	const { hasSeenUniversalCreateCwRExperimentSpotlight } = deserializeState(onboardingStateData);

	const anchorEl = useElementBySelector(MENU_SELECTOR, {
		disabled:
			Boolean(hasSeenUniversalCreateCwRExperimentSpotlight) || Boolean(isOnboardingStateError),
	});

	const handleSpotlightStart = useCallback(() => {
		setIsSpotlightActive(true);

		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'universalCreateCwRExperimentSpotlight',
				attributes: { source: ANALYTICS_SOURCE },
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const markSpotlightSeen = useCallback(() => {
		markSpotlightSeenInStorage();
		setOnboardingState({
			key: UNIVERSAL_CREATE_CWR_EXPERIMENT_SPOTLIGHT_ONBOARDING_STATE_KEYS.hasSeenUniversalCreateCwRExperimentSpotlight,
			value: 'true',
		});
	}, [markSpotlightSeenInStorage, setOnboardingState]);

	const handleSpotlightStop = useCallback(() => {
		setIsSpotlightActive(false);
	}, []);

	const choreographer = useChoreographer(CHOREOGRAPHER_MESSAGE_ID, {
		start: handleSpotlightStart,
		stop: handleSpotlightStop,
	});

	const startSpotlight = useCallback(() => {
		// No-op while loading: the onboarding-state query is cached after
		// it first resolves, so the next menu open will show the spotlight.
		if (
			isOnboardingStateLoading ||
			isOnboardingStateError ||
			hasSeenUniversalCreateCwRExperimentSpotlight
		) {
			return;
		}

		void choreographer.startMessage();
	}, [
		choreographer,
		hasSeenUniversalCreateCwRExperimentSpotlight,
		isOnboardingStateError,
		isOnboardingStateLoading,
	]);

	const stopSpotlight = useCallback(() => {
		void choreographer.stopMessage();
	}, [choreographer]);

	const handleDismiss = useCallback(
		(_event: DismissEvent) => {
			markSpotlightSeen();

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'universalCreateCwRExperimentSpotlightDismiss',
					source: ANALYTICS_SOURCE,
				},
			}).fire();

			void choreographer.stopMessage();
		},
		[choreographer, createAnalyticsEvent, markSpotlightSeen],
	);

	const handleCtaClick = useCallback(() => {
		markSpotlightSeen();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'universalCreateCwRExperimentSpotlightCTA',
				source: ANALYTICS_SOURCE,
			},
		}).fire();

		// Closing the menu via `onCtaDismissMenu` triggers the consumer's
		// `onClose` handler, which is responsible for invoking
		// `stopSpotlight` (and therefore `choreographer.stopMessage()`).
		// Calling `stopMessage()` here as well would result in a duplicate
		// stop, so we intentionally rely solely on `onCtaDismissMenu`.
		onCtaDismissMenu?.();
	}, [createAnalyticsEvent, onCtaDismissMenu, markSpotlightSeen]);

	const canRender =
		isMenuOpen &&
		anchorEl !== null &&
		!isOnboardingStateLoading &&
		!hasSeenUniversalCreateCwRExperimentSpotlight;

	const spotlight = canRender ? (
		<PopoverProvider>
			{createPortal(
				<Box aria-hidden testId="hidden-mounting-target" xcss={spotlightAnchorStyles.anchorWrapper}>
					<PopoverTarget>
						<Box as="span" />
					</PopoverTarget>
				</Box>,
				anchorEl,
			)}
			<Portal zIndex={600}>
				<PopoverContent dismiss={handleDismiss} isVisible={isSpotlightActive} placement="left-end">
					<SpotlightUpdateOnChange selectors={[MENU_SELECTOR]} />
					<SpotlightCard testId="universal-create-cwr-experiment-spotlight">
						<SpotlightHeader>
							<SpotlightHeadline>{formatMessage(i18n.title)}</SpotlightHeadline>
							<SpotlightControls>
								<SpotlightDismissControl />
							</SpotlightControls>
						</SpotlightHeader>
						<SpotlightMedia>
							<Image src={SplitButtonGif} alt="" width={295} height={135} />
						</SpotlightMedia>
						<SpotlightBody>
							<Stack space="space.150">
								<Text>
									<FormattedMessage {...i18n.bodyLine1} values={BODY_LINE1_VALUES} />
								</Text>
								<Text>{formatMessage(i18n.bodyLine2)}</Text>
							</Stack>
						</SpotlightBody>
						<SpotlightFooter>
							<SpotlightActions>
								<SpotlightPrimaryAction onClick={handleCtaClick}>
									{formatMessage(i18n.cta)}
								</SpotlightPrimaryAction>
							</SpotlightActions>
						</SpotlightFooter>
					</SpotlightCard>
				</PopoverContent>
			</Portal>
		</PopoverProvider>
	) : null;

	return { spotlight, startSpotlight, stopSpotlight };
};

export type UniversalCreateMenuSpotlightProps = {
	isMenuOpen: boolean;
	onStartSpotlightChange: (startSpotlight: (() => void) | null) => void;
	onStopSpotlightChange: (stopSpotlight: (() => void) | null) => void;
	onCtaDismissMenu?: () => void;
};

export const UniversalCreateMenuSpotlight = ({
	isMenuOpen,
	onStartSpotlightChange,
	onStopSpotlightChange,
	onCtaDismissMenu,
}: UniversalCreateMenuSpotlightProps) => {
	const { spotlight, startSpotlight, stopSpotlight } = useUniversalCreateMenuSpotlight({
		isMenuOpen,
		onCtaDismissMenu,
	});

	useEffect(() => {
		onStartSpotlightChange(startSpotlight);
		return () => onStartSpotlightChange(null);
	}, [onStartSpotlightChange, startSpotlight]);

	useEffect(() => {
		onStopSpotlightChange(stopSpotlight);
		return () => onStopSpotlightChange(null);
	}, [onStopSpotlightChange, stopSpotlight]);

	return <>{spotlight}</>;
};
